import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Linkedin, Facebook, Instagram, Twitter, Youtube, Shield } from "lucide-react";

export function Footer() {
  return (
    <footer className="bg-[#101319] text-[#9BA3B2] pt-16 md:pt-24 pb-8 border-t border-[#101319] select-none font-instrument">

      {/* Brand + Link Columns */}
      <div className="container mx-auto px-6 mb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-10">

          {/* Brand / Positioning */}
          <div className="space-y-6 lg:col-span-3">
            <div>
              <img src="/images/frayze-logo-light.webp" alt="Frayze Logo" width={384} height={115} loading="lazy" className="h-9 w-auto object-contain" />
              <p className="text-xs font-bold text-[#7C9AFB] uppercase tracking-widest mt-3 font-mono-ibm">Websites • CRM • Growth</p>
              <p className="mt-4 text-sm text-[#9BA3B2]/85 leading-relaxed font-semibold">
                Based in Thunder Bay. Building high-performance revenue systems for Northern Ontario businesses and lead-driven brands across Canada.
              </p>
            </div>

            <div className="space-y-3 text-sm text-[#9BA3B2] font-semibold">
              <div className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-[#2F5FE8] shrink-0 mt-0.5" />
                <span>202 Franklin St. North, Thunder Bay, ON P7C 4H9</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Phone className="w-4 h-4 text-[#2F5FE8] shrink-0" />
                <a href="tel:+18077000079" className="hover:text-white transition-colors">+1 807-700-0079</a>
              </div>
              <div className="flex items-center gap-2.5">
                <Mail className="w-4 h-4 text-[#2F5FE8] shrink-0" />
                <a href="mailto:hello@frayze.ca" className="hover:text-white transition-colors">hello@frayze.ca</a>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              {[
                "100% Canadian-Owned",
                "Trusted Across Ontario",
                "WCAG / AODA-Aware Builds"
              ].map((badge) => (
                <div key={badge} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[10px] font-semibold text-slate-300 shadow-sm">
                  <Shield className="w-3 h-3 text-[#2F5FE8]/70" />
                  {badge}
                </div>
              ))}
            </div>
          </div>

          {/* Link Columns */}
          <div className="lg:col-span-9 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-8 gap-y-10">

            {/* Start Here */}
            <div>
              <h3 className="font-bold mb-5 text-white font-instrument text-base">Start Here</h3>
              <ul className="space-y-3">
                {[
                  { name: "Start Here", href: "/start/" },
                  { name: "Website Launch", href: "/website-launch-program/" },
                  { name: "Free Digital Audit", href: "/digital-audit/" },
                  { name: "Pricing", href: "/pricing/" },
                  { name: "Case Studies", href: "/case-studies/" },
                  { name: "Service Areas", href: "/service-areas/" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Solutions */}
            <div>
              <h3 className="font-bold mb-5 text-white font-instrument text-base">Solutions</h3>
              <ul className="space-y-3">
                {[
                  { name: "AI Strategy & Rollout", href: "/solutions/ai-strategy-rollout/" },
                  { name: "Website Design", href: "/solutions/websites/" },
                  { name: "Local SEO & Reputation", href: "/solutions/local-seo-reputation/" },
                  { name: "CRM Automation", href: "/solutions/crm-automation/" },
                  { name: "Lead Generation System", href: "/solutions/lead-generation-system/" },
                  { name: "Ad Management", href: "/solutions/ad-management-scale/" },
                  { name: "All Solutions", href: "/solutions/" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industries */}
            <div>
              <h3 className="font-bold mb-5 text-white font-instrument text-base">Industries</h3>
              <ul className="space-y-3">
                {[
                  { name: "Contractors", href: "/industries/trades/" },
                  { name: "Automotive", href: "/automotive/" },
                  { name: "Independent Dealers", href: "/automotive/dealers/" },
                  { name: "GTA Dealers", href: "/automotive/gta-dealers/" },
                  { name: "Thunder Bay", href: "/service-areas/thunder-bay/" },
                  { name: "Sudbury", href: "/service-areas/sudbury/" },
                  { name: "Toronto", href: "/service-areas/toronto/" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Platform */}
            <div>
              <h3 className="font-bold mb-5 text-white font-instrument text-base">Platform</h3>
              <ul className="space-y-3">
                {[
                  { name: "What's Included", href: "/platform/" },
                  { name: "Missed-Call Text-Back", href: "/platform/missed-call-text-back/" },
                  { name: "Speed-to-Lead", href: "/platform/speed-to-lead/" },
                  { name: "AI Receptionist", href: "/platform/ai-receptionist/" },
                  { name: "Reputation Management", href: "/platform/reputation-management/" },
                  { name: "Database Reactivation", href: "/platform/database-reactivation/" },
                  { name: "Business Phone System", href: "/platform/business-phone-system/" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-bold mb-5 text-white font-instrument text-base">Company</h3>
              <ul className="space-y-3">
                {[
                  { name: "About", href: "/about/" },
                  { name: "Contact", href: "/contact/" },
                  { name: "Blog", href: "/blog/" },
                  { name: "What's New", href: "/updates/" },
                  { name: "Careers", href: "/careers/" },
                  { name: "Partners", href: "/partners/" },
                  { name: "FAQ", href: "/faq/" },
                  { name: "Resources", href: "/resources/" },
                ].map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href="https://app.frayze.ca"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-slate-400 hover:text-white transition-colors font-instrument"
                  >
                    Client Login
                  </a>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </div>

      {/* Bottom Micro Row */}
      <div className="container mx-auto px-6 py-8 border-t border-white/5 mt-8">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6 text-xs text-[#9BA3B2] font-semibold">
          <div className="flex flex-wrap justify-center md:justify-start gap-x-8 gap-y-3">
            {[
              { name: "Privacy Policy", href: "/privacy-policy/" },
              { name: "Terms & Conditions", href: "/terms/" },
              { name: "Usage & Billing", href: "/usage-billing/" },
              { name: "Acceptable Use Policy", href: "/acceptable-use/" },
              { name: "Sitemap", href: "/sitemap/" },
            ].map((link) => (
              <Link key={link.name} to={link.href} className="hover:text-white transition-colors font-instrument">
                {link.name}
              </Link>
            ))}
          </div>

          <div className="flex flex-col md:flex-row items-center gap-4 md:gap-6">
            <span>© {new Date().getFullYear()} Frayze Technologies Inc.</span>
            <div className="flex items-center gap-5">
              {[
                { icon: Linkedin, href: "https://www.linkedin.com/company/frayze/", label: "Frayze on LinkedIn" },
                { icon: Facebook, href: "https://facebook.com/frayze.ca", label: "Frayze on Facebook" },
                { icon: Instagram, href: "https://instagram.com/frayze.ca", label: "Frayze on Instagram" },
                { icon: Twitter, href: "https://x.com/FrayzeIncCanada", label: "Frayze on X" },
                { icon: Youtube, href: "https://youtube.com/@frayzetech", label: "Frayze on YouTube" },
              ].map((social, i) => (
                <a
                  key={i}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.label}
                  title={social.label}
                  className="hover:text-white transition-colors opacity-60 hover:opacity-100"
                >
                  <social.icon className="w-4 h-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}